import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

const SuccessModal = ({ credentials, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyPassword = () => {
    if (credentials?.password) {
      navigator?.clipboard?.writeText(credentials?.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your account has been activated and is ready to use
            </p>
          </div>

          {credentials?.pending ? (
            /* Pending Account Creation */
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200 mb-2">
                    Account Setup in Progress
                  </h3>
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                    Your payment has been processed successfully. Your account credentials will be sent to <strong>{credentials?.email}</strong> within the next 60 seconds.
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    Please check your email inbox (and spam folder) for your login details.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Account Created Successfully */
            <>
              {/* Account Details */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-200 mb-4">
                  Your Login Credentials
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-1">
                      Email Address
                    </label>
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700">
                      <p className="text-gray-900 dark:text-white font-mono">{credentials?.email}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-1">
                      Temporary Password
                    </label>
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700 flex items-center justify-between">
                      <p className="text-gray-900 dark:text-white font-mono">{credentials?.password}</p>
                      <button
                        onClick={handleCopyPassword}
                        className="ml-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                      >
                        {copied ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-white dark:bg-slate-700 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Important:</strong> Please change your password after your first login for security purposes.
                  </p>
                </div>
              </div>

              {/* Email Confirmation */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    A welcome email with these credentials has been sent to <strong>{credentials?.email}</strong>
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Subscription Details */}
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Subscription Details
            </h4>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p><strong>Company:</strong> {credentials?.companyName}</p>
              <p><strong>Plan:</strong> {credentials?.plan}</p>
              <p><strong>Status:</strong> <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Active</span></p>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Next Steps:
            </h4>
            <ol className="space-y-2">
              <li className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold mr-3">1</span>
                <span>Log in to your dashboard using the credentials above</span>
              </li>
              <li className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold mr-3">2</span>
                <span>Complete your company profile and settings</span>
              </li>
              <li className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold mr-3">3</span>
                <span>Import your employee data or add employees manually</span>
              </li>
              <li className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold mr-3">4</span>
                <span>Start managing your HR compliance and payroll</span>
              </li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              fullWidth
              onClick={onClose}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => window.print()}
            >
              Print Credentials
            </Button>
          </div>

          {/* Support */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Need help? Contact us at <a href="mailto:support@hrpacificrbs.com" className="text-emerald-600 dark:text-emerald-400 hover:underline">support@hrpacificrbs.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;